import { useEffect, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Box,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Button,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import Listtable from 'components/common/ListTable';
import useDebounce from 'hooks/useDebounce';
import {
  getPublicRegistrations,
  exportPublicRegistrations,
} from "../../api/publicRegistrations";

const STATUS_OPTIONS = [
  "OTP Pending",
  "OTP Verified",
  "Payment Pending",
  "Payment Failed",
  "Paid",
  "Activated",
];

const STATUS_STYLES = {
  "OTP Pending": { bg: "#F3F4F6", color: "#6B7280" },
  "OTP Verified": { bg: "#EDF2FE", color: "#4976F4" },
  "Payment Pending": { bg: "#FEF3C7", color: "#B45309" },
  "Payment Failed": { bg: "#FEE2E2", color: "#DC2626" },
  Paid: { bg: "#E0F2FE", color: "#0E7490" },
  Activated: { bg: "#EFFEED", color: "#399F08" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES["OTP Pending"];
  return (
    <Box
      sx={{
        bgcolor: s.bg,
        color: s.color,
        padding: "0.3rem 0.9rem",
        borderRadius: "25px",
        width: "max-content",
        fontWeight: 600,
        fontSize: "13px",
        whiteSpace: "nowrap",
      }}
    >
      {status || "-"}
    </Box>
  );
};

const titleCase = (str) =>
  str
    ? str
        .toLowerCase()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "-";

const tableHeader = [
  "S.No",
  "Name",
  "Contact",
  "Speciality",
  "Location",
  "Status",
  "Registration Date",
];
const keysName = [
  "sno",
  "name",
  "contact",
  "specialityText",
  "location",
  "status",
  "createdAt",
];

const RegisteredDoctorsList = () => {
  const { searchText } = useOutletContext();
  const debouncedSearch = useDebounce(searchText, 500);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = () => {
    setLoading(true);
    const payload = {
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter && { status: statusFilter }),
      page: pageNumber + 1,
      limit: rowsPerPage,
    };

    getPublicRegistrations(payload)
      .then((response) => {
        if (response?.status !== 200) {
          throw new Error(response?.data?.message || "Failed to fetch");
        }
        const list = (response?.data?.data?.items || []).map(
          (x, idx) => {
            const location =
              [x?.city, x?.state].filter(Boolean).join(", ") || "-";
            return {
              ...x,
              sno: pageNumber * rowsPerPage + idx + 1,
              name: titleCase(x?.name),
              contact: (
                <Box>
                  <Typography sx={{ fontSize: "14px", color: "#111827" }}>
                    {x?.phone || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#7D8DC4" }}>
                    {x?.email || "-"}
                  </Typography>
                </Box>
              ),
              specialityText: x?.speciality || "-",
              location,
              status: <StatusBadge status={x?.status} />,
            };
          }
        );
        setRows(list);
        setCount(response?.data?.data?.total || 0);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setCount(0);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Could not fetch registered doctors"
        );
      });
  };

  useEffect(() => {
    if (pageNumber !== 0) {
      setPageNumber(0);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPageNumber(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(0);
  };

  const handleExport = async () => {
    if (exporting) return;
    if (count === 0) {
      toast.warn("No records to export.");
      return;
    }
    try {
      setExporting(true);
      const payload = {
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
      };
      const response = await exportPublicRegistrations(payload);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "registered-doctors.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported successfully!");
    } catch (err) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ fontWeight: "500", fontSize: "1.1rem", margin: "1rem 0" }}>
        Registered Doctors
      </Box>

      <Stack
        direction="row"
        sx={{
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          margin: "1rem 0",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" sx={{ color: "#7D8DC4" }}>
          {count} record{count === 1 ? "" : "s"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={exporting || count === 0}
          sx={{
            bgcolor: "#194AF5",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#1540d6", boxShadow: "none" },
          }}
        >
          {exporting ? "Exporting…" : "Download Excel"}
        </Button>
      </Stack>

      <Listtable
        tableheader={tableHeader}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalCount={count}
        dataKeyName={keysName}
        tag="registered-doctors"
      />
    </>
  );
};

export default RegisteredDoctorsList;
